import { z } from 'zod';

/**
 * @rynora/contracts/regions — single source of truth for the
 * org-level data-residency region surface (Batch 3 PR #2).
 *
 * Shipped today (v0):
 *   * The schema enum is locked at three values.
 *   * The catalogue marks each region with a lifecycle status so the
 *     trust pages can render "GA" / "Design-partner alpha" / "Planned"
 *     honestly instead of pretending every region is live.
 *   * Every endpoint that mutates an org's residency (`POST /api/orgs/:orgId/region`)
 *     returns 412 `ORG_REGION_MIGRATION_UNAVAILABLE` in v0 — the
 *     contract is locked; the cross-region worker that fulfils the
 *     request lands in a follow-up PR.
 *
 * Region enum policy (read before adding a value):
 *
 *   * `us-phx`  — US Phoenix, Oracle Cloud. The single production
 *                cluster today. Every existing org backfills to this.
 *   * `eu-fra`  — EU Frankfurt, Oracle Cloud. Listed as
 *                `design-partner-only` until the second tenancy is
 *                provisioned, the per-region observability stack is
 *                stood up, and a first enterprise design partner has
 *                signed.
 *   * `ap-syd`  — Asia-Pacific Sydney. Listed as `planned`; no concrete
 *                provisioning plan. Kept in the enum because removing a
 *                publicly-promised region after the fact is a much
 *                larger trust hit than admitting it's planned.
 *
 * Adding a new region is a CHECK-constraint refresh in a follow-up
 * migration + a row in `REGIONS` below + a paragraph in the runbook.
 * Removing a region is a breaking change — don't.
 */

// ---------------------------------------------------------------------------
// Region id enum + zod schema. Keep aligned with migration 153's
// CHECK constraint on `organizations.home_region`.
// ---------------------------------------------------------------------------

export const REGION_IDS = ['us-phx', 'eu-fra', 'ap-syd'] as const;

export const RegionIdSchema = z.enum(REGION_IDS);
export type RegionId = z.infer<typeof RegionIdSchema>;

/**
 * Lifecycle status of a region.
 *
 *   * `ga`                    — generally available; any org can pick it on signup.
 *   * `design-partner-only`   — physical cluster exists OR is imminent; available
 *                               to design-partner orgs only via support escalation.
 *                               UI surfaces this with a "Contact us" CTA.
 *   * `planned`               — committed to publicly but no concrete delivery date.
 *                               Trust pages render with "ETA TBD" copy.
 */
export const RegionStatusSchema = z.enum(['ga', 'design-partner-only', 'planned']);
export type RegionStatus = z.infer<typeof RegionStatusSchema>;

/**
 * Stable, machine-readable description of one Rynora region. Kept in
 * the contracts package so backend (`GET /api/regions`) and frontend
 * (`/trust/regions` page + the privacy/subprocessor pages) share the
 * exact same source of truth.
 */
export const RegionDescriptorSchema = z.object({
  id: RegionIdSchema,
  status: RegionStatusSchema,

  /** Human-readable label for UI ("US Phoenix", "EU Frankfurt", "AP Sydney"). */
  displayName: z.string().min(1),

  /** Two-letter ISO 3166 code of the underlying datacenter country. */
  countryCode: z.string().length(2),

  /** Free-form jurisdiction description used in the sub-processor table. */
  jurisdiction: z.string().min(1),

  /** Oracle Cloud datacenter identifier, e.g. `us-phoenix-1`. */
  cloudRegion: z.string().min(1),

  /**
   * Customer-facing hostname (or empty string for not-yet-deployed
   * regions). Lets the frontend offer a "Take me to my region" link
   * once we go multi-cluster.
   */
  hostname: z.string(),

  /**
   * ISO date string of the region's expected (or actual, for `ga`)
   * general-availability date. Empty when no date has been committed.
   */
  gaDate: z.string(),

  /**
   * Anchor slug into the DPA document that names this region's
   * sub-processor footprint. Empty when the DPA hasn't been amended
   * yet for this region.
   */
  dpaAnchor: z.string(),

  /** Per-region status page URL. Empty when there is no separate status page. */
  statusPageUrl: z.string(),
});
export type RegionDescriptor = z.infer<typeof RegionDescriptorSchema>;

/**
 * The canonical catalogue. Keep in sync with:
 *
 *   * Migration 153's CHECK constraint on `organizations.home_region`.
 *   * `packages/landing-page/src/features/legal/pages/RegionsPage.tsx`.
 *   * `packages/landing-page/src/features/legal/pages/SubprocessorsPage.tsx`
 *     (the sub-processor jurisdiction column).
 *   * `docs/handbook/architecture/regions.md` (the design doc).
 *
 * The catalogue is intentionally static (not DB-backed) in v0:
 *
 *   * Regions are launched by an engineering PR, not by an operator
 *     flipping a setting — so the source of truth belongs in code.
 *   * The trust pages need to render honestly even when the API is
 *     temporarily unreachable, so the frontend ships its own copy of
 *     the catalogue (re-exported from this module).
 *
 * The `GET /api/regions` endpoint simply returns this array verbatim
 * with the deployed cluster's `currentRegion` flag layered on top
 * (see `RegionsCatalogueResponseSchema` below).
 */
export const REGIONS: ReadonlyArray<RegionDescriptor> = [
  {
    id: 'us-phx',
    status: 'ga',
    displayName: 'US Phoenix',
    countryCode: 'US',
    jurisdiction: 'United States — Arizona (Oracle Cloud us-phoenix-1)',
    cloudRegion: 'us-phoenix-1',
    hostname: 'app.rynora.app',
    gaDate: '2025-09-01',
    dpaAnchor: 'dpa-region-us-phx',
    statusPageUrl: 'https://status.rynora.app',
  },
  {
    id: 'eu-fra',
    status: 'design-partner-only',
    displayName: 'EU Frankfurt',
    countryCode: 'DE',
    jurisdiction: 'European Union — Germany (Oracle Cloud eu-frankfurt-1)',
    cloudRegion: 'eu-frankfurt-1',
    hostname: '',
    gaDate: '',
    dpaAnchor: 'dpa-region-eu-fra',
    statusPageUrl: '',
  },
  {
    id: 'ap-syd',
    status: 'planned',
    displayName: 'AP Sydney',
    countryCode: 'AU',
    jurisdiction: 'Asia-Pacific — Australia (Oracle Cloud ap-sydney-1)',
    cloudRegion: 'ap-sydney-1',
    hostname: '',
    gaDate: '',
    dpaAnchor: '',
    statusPageUrl: '',
  },
] as const;

/** Lookup helper. Throws on unknown id — callers should validate first via RegionIdSchema. */
export function getRegionDescriptor(id: RegionId): RegionDescriptor {
  const found = REGIONS.find((r) => r.id === id);
  if (!found) {
    throw new Error(`Unknown region id: ${id} (this is a contract bug — REGIONS catalogue is out of sync)`);
  }
  return found;
}

// ---------------------------------------------------------------------------
// API contracts.
// ---------------------------------------------------------------------------

/**
 * Response from `GET /api/regions` (public, no auth).
 *
 *   * `regions`          — the static catalogue above (every region we list publicly).
 *   * `currentRegion`    — which region the cluster this API is deployed in is serving.
 *                          v0 hard-codes this to `us-phx` via admin_config; future
 *                          clusters declare their own.
 *   * `availableRegions` — the subset of region ids that the operator has marked as
 *                          live in admin_config. v0 ships `['us-phx']`. Used by the
 *                          signup form to decide which regions to show as pickable.
 */
export const RegionsCatalogueResponseSchema = z.object({
  regions: z.array(RegionDescriptorSchema),
  currentRegion: RegionIdSchema,
  availableRegions: z.array(RegionIdSchema).min(1),
});
export type RegionsCatalogueResponse = z.infer<typeof RegionsCatalogueResponseSchema>;

/**
 * Response from `GET /api/orgs/:orgId/region`.
 *
 * The shape is deliberately a strict superset of what the org-settings
 * UI needs to render the residency badge — so a frontend can show the
 * jurisdiction string + the country flag without a second fetch.
 */
export const OrgRegionStateSchema = z.object({
  orgId: z.string().uuid(),
  homeRegion: RegionIdSchema,
  descriptor: RegionDescriptorSchema,
  /**
   * Most-recent migration history row (regardless of state). Null when
   * the org has never had a migration request.
   */
  lastMigration: z
    .object({
      id: z.string().uuid(),
      fromRegion: RegionIdSchema,
      toRegion: RegionIdSchema,
      state: z.enum(['requested', 'in_progress', 'completed', 'failed', 'cancelled']),
      reason: z.string().nullable(),
      requestedAt: z.string(),
      completedAt: z.string().nullable(),
      failedAt: z.string().nullable(),
      cancelledAt: z.string().nullable(),
      requestedByUserName: z.string().nullable(),
    })
    .nullable(),
});
export type OrgRegionState = z.infer<typeof OrgRegionStateSchema>;

/**
 * Body for `POST /api/orgs/:orgId/region` (owner-only).
 *
 * v0 always returns 412 `ORG_REGION_MIGRATION_UNAVAILABLE` but the
 * contract is shipped so the frontend form, the audit-log event
 * payload, and the future migration worker share one schema.
 */
export const OrgRegionMigrateBodySchema = z.object({
  toRegion: RegionIdSchema,
  /**
   * Customer-supplied reason. Surfaced to support; written to
   * `org_region_history.reason` and to the audit-log row's `meta`.
   */
  reason: z.string().min(10).max(2000),
  /**
   * Owner must type the destination region's display name verbatim
   * (case-insensitive) to confirm a destructive cross-region move.
   * Guards against accidental submissions from CI scripts or the API
   * console. The same pattern is used by the BYOK disable form.
   */
  confirmation: z.string().min(1),
});
export type OrgRegionMigrateBody = z.infer<typeof OrgRegionMigrateBodySchema>;
